import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { range, sortBy } from 'lodash'
import { dateTimeFormat } from '../shared/utilities/date'
import { format } from 'date-fns'
import { IdrColumnsResponse, IdrResult } from './idr-investigation.service'
import { ReferenceDataSource } from '../public/reference-data/base-reference.service'
import { IdrColumnVisibility } from './idr-user-settings.service'

export type IdrColumnType = 'required' | 'default' | 'optional'

export type IdrColumnDataType = 'string' | 'boolean' | 'decimal' | 'date' | 'timestamp'

type UsageUnits = 'kwh'

type IdrStaticColumnSpec = Readonly<{
  field: string
  width?: number
  columnType: IdrColumnType
  numberFormat?: Intl.NumberFormat
  usageUnits?: UsageUnits
  hourlyUsage?: boolean
}>

export type IdrColumnSpec = IdrStaticColumnSpec & Readonly<{
  friendlyName: string
  dataType: IdrColumnDataType
}>

type StaticColumnSpecsMap = Readonly<Record<string, IdrStaticColumnSpec>>

const usageFractionDigits = 3

const usageNumberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: usageFractionDigits,
  maximumFractionDigits: usageFractionDigits,
})

const minColumnWidth = 130
const defaultColumnWidth = 200

const getHourlyUsageSpec = (hour: number): IdrStaticColumnSpec => {
  const field = `h${hour < 10 ? '0' : ''}${hour}`
  return {
    field,
    columnType: 'optional',
    numberFormat: usageNumberFormat,
    usageUnits: 'kwh',
    hourlyUsage: true,
    width: minColumnWidth,
  }
}

const staticColumnSpecs: readonly IdrStaticColumnSpec[] = [
  {
    field: 'service_point_id',
    columnType: 'required',
  },
  {
    field: 'operating_date',
    columnType: 'required',
  },
  {
    field: 'settlement_type',
    columnType: 'required',
  },
  {
    field: 'usage_total',
    columnType: 'required',
    numberFormat: usageNumberFormat,
    usageUnits: 'kwh',
  },
  ...range(0, 24).map(i => getHourlyUsageSpec(i + 1)),
  {
    field: 'measurement_value_type_name',
    columnType: 'default',
  },
  {
    field: 'cpnode_code',
    columnType: 'default',
  },
  {
    field: 'proxy_date',
    columnType: 'default',
  },
  {
    field: 'proxy_date_diff_weeks',
    columnType: 'optional',
  },
  {
    field: 'proxy_date_diff_days',
    columnType: 'optional',
  },
  {
    field: 'bill_start_date',
    columnType: 'optional',
  },
  {
    field: 'bill_stop_date',
    columnType: 'optional',
  },
  {
    field: 'control_area_code',
    columnType: 'default',
  },
  {
    field: 'location_code',
    columnType: 'default',
  },
  {
    field: 'cpnode_min_limit',
    columnType: 'optional',
  },
  {
    field: 'cpnode_max_limit',
    columnType: 'optional',
  },
  {
    field: 'company_code',
    columnType: 'default',
  },
  {
    field: 'ldc_code',
    columnType: 'optional',
  },
  {
    field: 'eldc_code',
    columnType: 'optional',
  },
  {
    field: 'profile_class_code',
    columnType: 'optional',
  },
  {
    field: 'retailer_code',
    columnType: 'optional',
  },
  {
    field: 'retailer_type',
    columnType: 'optional',
  },
  {
    field: 'supply_class_code',
    columnType: 'default',
  },
  {
    field: 'loss_class_code',
    columnType: 'optional',
  },
  {
    field: 'delivery_class_code',
    columnType: 'optional',
  },
  {
    field: 'market_participant_code',
    columnType: 'optional',
  },
  {
    field: 'supply_group_code',
    columnType: 'optional',
  },
  {
    field: 'tie_type',
    columnType: 'optional',
  },
  {
    field: 'net_meter_rider_code',
    columnType: 'default',
  },
  {
    field: 'tie_flag',
    columnType: 'optional',
  },
  {
    field: 'mdma_add_to_adjusted_system_load_flag',
    columnType: 'optional',
  },
  {
    field: 'mdma_aggregation_exclusion_flag',
    columnType: 'optional',
  },
  {
    field: 'mdma_subtract_from_adjusted_system_load_flag',
    columnType: 'optional',
  },
  {
    field: 'mdma_weather_sensitive_flag',
    columnType: 'optional',
  },
]

const getColumnOrdering = (): Readonly<Record<string, number>> => {
  const ordering: Record<string, number> = {}
  staticColumnSpecs.forEach((s, i) => {
    ordering[s.field] = i
  })
  return ordering
}

const columnOrdering = getColumnOrdering()

const getStaticColumnSpecMap = (): StaticColumnSpecsMap => {
  const result: Record<string, IdrStaticColumnSpec> = {}
  staticColumnSpecs.forEach(columnSpec => {
    result[columnSpec.field] = columnSpec
  })
  return result
}

const staticColumnSpecMap = getStaticColumnSpecMap()

export type IdrColumnMetadata = Readonly<{
  columnSpecs: readonly IdrColumnSpec[]
  columnSpecsByField: Readonly<Record<string, IdrColumnSpec>>
}>

const getColumnSpecs = (response: IdrColumnsResponse): readonly IdrColumnSpec[] => {
  const columnSpecs: IdrColumnSpec[] = []
  Object.keys(response.table_columns).forEach(field => {
    const staticColumnSpec = staticColumnSpecMap[field]
    const { friendly_name, data_type } = response.table_columns[field]
    if (staticColumnSpec) {
      columnSpecs.push({
        ...staticColumnSpec,
        friendlyName: friendly_name,
        dataType: data_type,
      })
    }
  })
  return columnSpecs
}

export const getIdrColumnMetadata = (response: IdrColumnsResponse): IdrColumnMetadata => {
  const columnSpecs = getColumnSpecs(response)
  const columnSpecsByField: Record<string, IdrColumnSpec> = {}
  columnSpecs.forEach(columnSpec => {
    columnSpecsByField[columnSpec.field] = columnSpec
  })
  return { columnSpecs, columnSpecsByField }
}

export type IdrColDef = ColDef & { field: string, hide: boolean }

const columnDefDefaults: Partial<IdrColDef> = {
  filter: true,
  sortable: true,
  editable: false,
  minWidth: minColumnWidth,
  width: defaultColumnWidth,
}

const columnTypeDefaults: Readonly<Record<IdrColumnDataType, Partial<ColDef>>> = {
  string: {},
  boolean: {
    valueFormatter: ({ value }: ValueFormatterParams): string  => {
      if (value == null) {
        return ''
      } else {
        return value ? 'Yes' : 'No'
      }
    },
  },
  decimal: {
    cellClass: 'text-end',
  },
  date: {},
  timestamp: {
    valueFormatter: ({ value }: ValueFormatterParams): string  =>
      value ? format(value, dateTimeFormat) : '',
  },
}

type ColDefCellType = 'text' | 'number' | 'boolean' | 'date' | 'dateString'

const colDefTypes: Readonly<Record<IdrColumnDataType, ColDefCellType>> = {
  string: 'text',
  decimal: 'number',
  boolean: 'boolean',
  timestamp: 'text',
  date: 'dateString',
}

const isHidden = (c: IdrColumnSpec, visibility: IdrColumnVisibility): boolean => {
  if (c.columnType === 'required') {
    // ignore whatever is persisted... can't hide this column
    return false
  } else {
    const configuredVisibility = visibility[c.field]
    return configuredVisibility === undefined ? c.columnType === 'optional' : !configuredVisibility
  }
}

export const getUsageHeaderSuffix = (units?: UsageUnits) =>
  units === 'kwh' ? ' (kWh)' : ''

const getValueFormatter = (spec: IdrColumnSpec): Partial<IdrColDef> => {
  const { numberFormat } = spec
  if (numberFormat) {
    return {
      valueFormatter: ({ value }: ValueFormatterParams<IdrResult, number>) =>
        value == null ? '': numberFormat.format(value),
    }
  } else {
    return {}
  }
}

const getColDef = (spec: IdrColumnSpec, visibility: IdrColumnVisibility): IdrColDef => ({
  headerName: spec.friendlyName + getUsageHeaderSuffix(spec.usageUnits),
  ...columnDefDefaults,
  ...columnTypeDefaults[spec.dataType],
  field: spec.field,
  cellDataType: colDefTypes[spec.dataType],
  hide: isHidden(spec, visibility),
  ...getValueFormatter(spec),
  ...spec.width ? { width: spec.width } : {},
})

export const getIdrColDefs = (
  columnSpecs: readonly IdrColumnSpec[],
  visibility: IdrColumnVisibility,
): IdrColDef[] =>
  sortBy(columnSpecs, _ => columnOrdering[_.field])
    .map(col => getColDef(col, visibility))

export type ReferenceDataSpec = Readonly<{
  source: ReferenceDataSource
  aliasFor?: string
  entityProperty: 'entity_code' | 'entity_name'
}>

const getSpec = (
  source: ReferenceDataSource,
  entityProperty: 'entity_code' | 'entity_name' = 'entity_code',
): ReferenceDataSpec => ({ source, entityProperty })


export const referenceDataSpecs: Readonly<Record<string, ReferenceDataSpec>> = ({
  settlement_type: getSpec('settlementTypes', 'entity_name'),
  measurement_value_type_name: getSpec('measurementValueTypes', 'entity_name'),
  cpnode_code: getSpec('cpNodes'),
  control_area_code: getSpec('controlAreas'),
  location_code: getSpec('locations'),
  company_code: getSpec('companies'),
  ldc_code: getSpec('localDistributionCompanies'),
  eldc_code: { ...getSpec('localDistributionCompanies'), aliasFor: 'ldc_code' },
  profile_class_code: getSpec('profileClasses'),
  retailer_code: getSpec('retailers'),
  supply_class_code: getSpec('supplyClasses'),
  loss_class_code: getSpec('lossClasses'),
  delivery_class_code: getSpec('deliveryClasses'),
})
